import $ from 'jquery';

import { logger } from 'cadenza/utils/logging';

/**
 * A number, or a string containing a number.
 *
 * @typedef {{
 *  gazetteerProviders: Array;
 *  useCurrentBBox: boolean
 * }} GazetteerConfiguration
 */

/**
 * Creates a gazetteerDropdown underneath the button.
 *
 * @param {HTMLButtonElement} buttonResponsibleForOpeningDropdown - We are passing the button that is responsible
 * for opening dropdown because of following reasons:
 * 1. It is used to calculate where to show the dropdown (under the button).
 * 2. For setting the correct attribute value for 'aria-expanded'.
 * @param {HTMLInputElement} gazetteerSearchField - The search field is necessary so that we can trigger the autocompletion.
 * @param {GazetteerConfiguration} gazetteerConfiguration - gazetteerConfiguration that will be used for this dropdown.
 * @param {object} options - Additional options.
 * @param {(useCurrentBBox: boolean, selectedProviders: string[]) => void} [options.onChange] -
 *  Callback that is executed when the gazetteer config was changed.
 * @param {boolean} [options.showHeader] - Boolean that says whether we want to show additional heading 'Search for:'
 */
export function createGazetteerDropdown (
  buttonResponsibleForOpeningDropdown, // Needed so that we can calculate the position of the dropdown.
  gazetteerSearchField,
  gazetteerConfiguration,
  options = {}
) {
  if (gazetteerConfiguration.gazetteerProviders.length === 0) {
    logger.warn('No gazetteer suggestion providers are available.');
    return;
  }

  const {
    onChange = () => {},
    showHeader = true
  } = options;
  import(/* webpackChunkName: "gazetteer-providers-dropdown" */ 'cadenza/map/gazetteer/gazetteer-providers-dropdown')
    .then(({ GazetteerProvidersDropdown }) => {
      buttonResponsibleForOpeningDropdown
        .setAttribute('aria-expanded', true);
      showGazetteerDropdown(
        GazetteerProvidersDropdown,
        buttonResponsibleForOpeningDropdown,
        gazetteerSearchField,
        gazetteerConfiguration,
        showHeader,
        onChange);
    })
    .catch((error) => {
      logger.error('Failed to show providers dropdown', error);
    });
}

function showGazetteerDropdown (
  GazetteerProvidersDropdown,
  buttonResponsibleForOpeningDropdown,
  gazetteerSearchField,
  gazetteerConfiguration,
  showHeader,
  onChange) {
  // Create dropdown.
  const gazetteerProvidersDropdown = new GazetteerProvidersDropdown(
    gazetteerConfiguration,
    {
      withHeading: showHeader,
      onClose: () => {
        // aria-expanded set to false in timeout to prevent popup to be reopened after clicking on button
        setTimeout(() => buttonResponsibleForOpeningDropdown.setAttribute('aria-expanded', 'false'), 200);
        if (gazetteerProvidersDropdown.selectedProvidersChanged
          || gazetteerProvidersDropdown.useCurrentBBoxChanged) {
          $(gazetteerSearchField)
            .focus()
            .gazetteerAutocomplete('search');
        }
      },
      onChange
    });

  // Show dropdown.
  gazetteerProvidersDropdown.show(buttonResponsibleForOpeningDropdown);
}

export function isGazetteerSearchRestricted (gazetteerConfiguration) {
  return gazetteerConfiguration.useCurrentBBox // Search only in current extent
    || gazetteerConfiguration.gazetteerProviders.some(({ checked }) => !checked); // Not all active
}
